import type { IconName } from '@tunasong/icons'
import { useExportReadyAlert } from '@tunasong/plugin-export'
import type { BaseLayoutSpec } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { FullPageProgress, HBox, useUIMode, VBox } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { Suspense, useEffect, useEffectEvent } from 'react'
import { getDevTitle } from '../dev-worktree.js'
import styles from './app-chrome.module.css'
import { AppSideBar } from './app-sidebar.js'
import { MobileNavigation } from './mobile-navigation.js'
import { PageHeader } from './page-header.js'
import { usePageMetadata } from './page-metadata.js'

interface AppChromeProps {
  layouts?: BaseLayoutSpec[]
  entity?: Persisted<Entity>
  showNavigationBar?: boolean | 'dynamic'
  showPageHeader?: boolean
  currentLayout?: string
  className?: string

  /** Meta data  */
  pageTitle?: string
  pageDescription?: string
  pageIconName?: IconName

  children?: ReactNode
}

export const AppChrome: FC<AppChromeProps> = ({
  entity,
  showNavigationBar: barMode = 'dynamic',
  showPageHeader = true,
  children,
  pageTitle: providedPageTitle,
  pageDescription: providedPageDescription,
  pageIconName: providedPageIconName,
  layouts = [],
  currentLayout,
}) => {
  const { name, pageIconName: iconName, description, setPageIconName, setDescription, setName } = usePageMetadata()

  const showBackButton = Boolean(entity)

  const metadataEvent = useEffectEvent(
    ({ name, description, iconName }: { name?: string; description?: string; iconName?: IconName }) => {
      if (name) {
        setName(name)
      }
      if (description) {
        setDescription(description)
      }
      if (iconName) {
        setPageIconName(iconName)
      }
    }
  )

  // Set the page metadata state when pageTitle, pageDescription or pageIconName changes
  useEffect(() => {
    metadataEvent({
      name: providedPageTitle,
      description: providedPageDescription,
      iconName: providedPageIconName,
    })
  }, [providedPageDescription, providedPageIconName, providedPageTitle])

  const layoutSpec = layouts.find(layout => layout.name === currentLayout) ?? layouts[0]

  const { appChrome = 'default' } = layoutSpec ?? {}

  const showChrome = appChrome !== 'none'

  const uiMode = useUIMode()
  useExportReadyAlert()

  const showNavigationBar = layoutSpec?.showNavigationBar
    ? true
    : barMode === 'dynamic'
      ? uiMode === 'mobile' && showChrome
      : Boolean(barMode)

  const content = (
    <>
      <main className={styles.contentColumn}>
        {showPageHeader ? <PageHeader name={name} pageIconName={iconName} showBackButton={showBackButton} /> : null}
        <VBox
          className={styles.contentBox}
          style={{
            paddingTop: showPageHeader ? 'calc(52px + var(--app-dev-banner-h, 0px))' : 'var(--app-dev-banner-h, 0px)',
            paddingBottom: showNavigationBar ? 56 : 0,
          }}
        >
          {name && <title>{getDevTitle(name)}</title>}
          {description && <meta name="description" content={description} />}
          {children}
          {showNavigationBar ? <MobileNavigation entity={entity} /> : null}
        </VBox>
      </main>
    </>
  )

  return (
    <Suspense fallback={<FullPageProgress />}>
      {showChrome ? (
        <VBox className={styles.outerContainer}>
          <HBox className={styles.innerRow}>
            {uiMode === 'full' && appChrome !== 'mobile' && <AppSideBar entity={entity} />}
            {content}
          </HBox>
        </VBox>
      ) : (
        <>{content}</>
      )}
    </Suspense>
  )
}
